import type { StructuredResponse, StructuredSection } from './types';
import { ClinicalMarkdownBody } from './clinicalMarkdownComponents';
import { DataTable } from './DataTable';
import { S2Card } from './S2Card';

type SummarySection =
  | { title: string; kind: 'table'; columns: string[]; rows: Record<string, unknown>[] }
  | { title: string; kind: 'text'; body: string };

function stringifyItem(item: unknown): string {
  if (item == null) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);
  if (typeof item === 'object' && !Array.isArray(item)) {
    const obj = item as Record<string, unknown>;
    const body = obj.body_markdown ?? obj.content ?? obj.summary ?? obj.text ?? obj.value;
    if (typeof body === 'string') return body.trim();
    const title = obj.title ?? obj.name ?? obj.display_name;
    if (typeof title === 'string') return title.trim();
  }
  return '';
}

function sectionTitle(section: StructuredSection): string {
  if (typeof section.title === 'string' && section.title.trim()) return section.title.trim();
  const first = Array.isArray(section.items) ? section.items[0] : undefined;
  if (first && typeof first === 'object' && !Array.isArray(first)) {
    const title = (first as Record<string, unknown>).title;
    if (typeof title === 'string' && title.trim()) return title.trim();
  }
  // No real title anywhere - leave blank rather than showing a meaningless
  // "Section N" placeholder (e.g. closing-note paragraphs are intentionally untitled).
  return '';
}

function sectionBody(section: StructuredSection): string {
  if (typeof section.content === 'string' && section.content.trim()) return section.content.trim();
  if (Array.isArray(section.items) && section.items.length) {
    const itemBodies = section.items.map(stringifyItem).filter(Boolean);
    if (itemBodies.length) return itemBodies.join('\n\n');
  }
  return 'Nothing documented in retrieved chart excerpt.';
}

function normalizeSummarySections(structured: StructuredResponse): SummarySection[] {
  const sections = structured.sections ?? [];
  return sections
    .map((section): SummarySection => {
      const title = sectionTitle(section);
      // #77: a type:"table" section carries real row data - render it as an
      // actual table (same DataTable ResponseSection.tsx uses for
      // structured_table_card), never flatten rows to dash-joined text. When
      // the backend omits an explicit columns array, derive one from the
      // row keys so the table still renders instead of falling back to text.
      if (section.type === 'table' && section.rows?.length) {
        const columns = section.columns?.length
          ? section.columns
          : Array.from(new Set(section.rows.flatMap((row) => Object.keys(row))));
        return { title, kind: 'table', columns, rows: section.rows };
      }
      return { title, kind: 'text', body: sectionBody(section) };
    })
    .filter((section) => section.title || (section.kind === 'text' && section.body) || (section.kind === 'table' && section.rows.length));
}

/**
 * Narrative variant of the canonical S2Card (ticket #101/WS-5): titled prose
 * sections with dividers. Previously its own gray, no-chevron card anatomy -
 * now a thin wrapper that delegates the bordered-card/header/chevron/collapse
 * shell to S2Card, so this content family shares the exact same Section-2
 * anatomy as ClinicalFactsPanel (table/list) content.
 */
export function ClinicalSummaryCard({ structured }: { structured: StructuredResponse }) {
  const sections = normalizeSummarySections(structured);
  const title = structured.title?.trim() || 'Patient Chart Overview';

  return (
    <S2Card title={title} count={sections.length} unit="section">
      <div className="divide-y divide-border/45 -mx-4 -mb-3.5 -mt-3">
        {sections.map((section, index) => (
          <section key={`${section.title}-${index}`} className="px-4 py-4">
            {section.title && (
              <div className="mb-2 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-primary/70" />
                <h4 className="text-[12px] font-medium uppercase tracking-widest text-text-tertiary">
                  {section.title}
                </h4>
              </div>
            )}
            {section.kind === 'table' ? (
              <DataTable columns={section.columns} rows={section.rows} compact={section.rows.length >= 8} />
            ) : (
              <div
                className="prose prose-sm dark:prose-invert max-w-none leading-relaxed
                  prose-p:my-1.5 prose-p:text-[13.5px] prose-p:leading-[1.75]
                  prose-ul:my-2 prose-ul:space-y-1 prose-li:my-0 prose-li:text-[13.5px] prose-li:leading-[1.7]
                  prose-ol:my-2 prose-strong:text-foreground prose-strong:font-semibold
                  prose-headings:mt-2 prose-headings:mb-1 prose-headings:text-foreground"
              >
                <ClinicalMarkdownBody content={section.body} className="" />
              </div>
            )}
          </section>
        ))}
      </div>
    </S2Card>
  );
}
